import { AppProps } from 'next/app';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';

import { Provider } from 'react-redux';

import { FeatureToggleEnum } from '@nocowanie/core/enums/feature-toggle.enum';
import { Toaster } from '@nocowanie/common-ui/components/molecules/toaster';

import { commonUiStateSlice } from '@app/app-state';
import { ThirdPartyScripts } from '@app/components/atoms/third-party-scripts';
import { AppHeader } from '@app/components/organisms/app-header';
import { AppHeaderOld } from '@app/components/organisms/app-header-old';
import { GrowthbookPagesProvider } from '@app/components/providers/growthbook-provider/growthbook-pages.provider';
import { environment } from '@app/environments/environment';
import { setMobile } from '@app/helpers/ssr.helpers';
import { useRemoveFragmentsOnMount } from '@app/hooks/useRemoveFragmentsOnMount';
import { PageProps } from '@app/models';
import nextI18nextConfig from '@app/next-i18next.config';
import { GrowthbookService } from '@app/services/growthbook.service';
import { setMobileVersion } from '@app/state/slices/common-ui-state.slice';
import { AppStore, nocowanieAppStateWrapper } from '@app/state/state';
import { ThemeProvider } from '@app/ui-providers';

import speculationRules from '../public/speculation-rules.json';

// Rendered inside <Provider store={store}> so that `useHydration`
// has react-redux context available (needed to read Growthbook context)
function NocowanieAppContent({
    store,
    props,
    Component,
}: {
    store: AppStore;
    props: PageProps;
    Component: AppProps<PageProps>['Component'];
}) {
    nocowanieAppStateWrapper.useHydration(props);

    const payload = commonUiStateSlice.selectors.selectGrowthbookPayload(store.getState());
    const forcedFeatures = commonUiStateSlice.selectors.selectFt(store.getState());
    const confirmationEmailId = payload
        ? GrowthbookService.instance.getFeatureValueFromPayload<string | undefined>(
              payload,
              FeatureToggleEnum.SM_CONFIRMATION_EMAIL_ID,
              undefined,
          )
        : undefined;

    return (
        <>
            <ThirdPartyScripts
                gtm={{
                    tagId: environment.GTM.GTM_TAG_ID,
                    auth: environment.GTM.GTM_AUTH,
                    preview: environment.GTM.GTM_PREVIEW,
                }}
                wphPixelId={environment.WPH_PIXEL_ID}
                sm={confirmationEmailId ? { confirmationEmailId } : undefined}
            />
            <ThemeProvider>
                <GrowthbookPagesProvider payload={payload} forcedFeatures={forcedFeatures}>
                    {props.hideAppHeader ? null : props.appHeaderProps?.isHomePage ? (
                        <AppHeader {...props.appHeaderProps} />
                    ) : (
                        <AppHeaderOld {...props.appHeaderProps} />
                    )}
                    <Component {...props} />
                    <Toaster />
                </GrowthbookPagesProvider>
            </ThemeProvider>
        </>
    );
}

function NocowanieApp({ Component, ...rest }: AppProps<PageProps>) {
    const store = nocowanieAppStateWrapper.useStore();
    const { pageProps: props } = rest;

    useRemoveFragmentsOnMount();

    setMobile({
        userAgent: props.userAgent,
        store,
        action: setMobileVersion,
    });

    return (
        <>
            <Provider store={store}>
                <NocowanieAppContent store={store} props={props} Component={Component} />
            </Provider>
            <Script
                id="speculation-rules"
                type="speculationrules"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(speculationRules),
                }}
            />
        </>
    );
}

// We don't define `getInitialProps` due to https://github.com/vercel/next.js/discussions/47501#discussioncomment-5420368.
// If it will be needed, we will most propably have to 'duplicate' 500 page in `_error` page.

// https://github.com/i18next/next-i18next/issues/990
export default appWithTranslation(NocowanieApp, nextI18nextConfig);
